import React from 'react';
import { ecoSystemServices } from '../data/mock';
import { Music, Disc3, Film, TrendingUp, Camera, Users, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';

const iconMap = {
  Music: Music,
  Disc3: Disc3,
  Film: Film,
  TrendingUp: TrendingUp,
  Camera: Camera,
  Users: Users
};

export const EcoSystem = () => {
  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-20 fade-in-section">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Sparkles className="text-white w-6 h-6" />
            <span className="text-white/60 text-sm tracking-widest uppercase">
              How It Works
            </span>
          </div>
          <h1 className="text-6xl md:text-7xl font-bold text-white mb-6">
            The <span style={{ color: '#00A672' }}>Eco-System</span>
          </h1>
          <p className="text-xl text-white/70 max-w-3xl mx-auto leading-relaxed">
            Six integrated services working seamlessly together. 
            Everything an artist needs to create, distribute, and perform at the highest level.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
          {ecoSystemServices.map((service, index) => {
            const Icon = iconMap[service.icon];
            return (
              <div
                key={service.id}
                className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-zinc-900 to-black border border-white/10 p-8 transition-all duration-500 hover:scale-105 hover:border-white/20"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Gradient Background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                
                {/* Content */}
                <div className="relative z-10">
                  <div className="w-14 h-14 rounded-2xl bg-white/5 backdrop-blur-md flex items-center justify-center mb-6 group-hover:bg-white/10 transition-colors duration-300">
                    <Icon size={28} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">
                    {service.title}
                  </h3>
                  <p className="text-white/70 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* The Journey Section */}
        <div className="mb-20">
          <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-16">
            Your Journey With BEKÂR
          </h2>
          
          <div className="max-w-4xl mx-auto space-y-12">
            {/* Step 1 */}
            <div className="flex gap-6 items-start fade-in-section">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-white text-black flex items-center justify-center font-bold text-lg">
                1
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-3">Create</h3>
                <p className="text-white/70 leading-relaxed">
                  Work with world-class producers in our cutting-edge studios. We help you craft 
                  your sound with immersive production techniques and state-of-the-art equipment.
                </p>
              </div>
            </div>

            {/* Arrow */}
            <div className="flex justify-center">
              <ArrowRight size={32} className="text-white/30 rotate-90" />
            </div>

            {/* Step 2 */}
            <div className="flex gap-6 items-start fade-in-section">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-white text-black flex items-center justify-center font-bold text-lg">
                2
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-3">Distribute & Sync</h3>
                <p className="text-white/70 leading-relaxed">
                  Get your music on every platform worldwide through our label. Our publishing team 
                  secures sync placements in film, TV, and advertising to maximize your reach.
                </p>
              </div>
            </div>

            {/* Arrow */}
            <div className="flex justify-center">
              <ArrowRight size={32} className="text-white/30 rotate-90" />
            </div>

            {/* Step 3 */}
            <div className="flex gap-6 items-start fade-in-section">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-white text-black flex items-center justify-center font-bold text-lg">
                3
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-3">Amplify</h3>
                <p className="text-white/70 leading-relaxed">
                  Our marketing team creates strategic campaigns that build your fanbase. Professional 
                  photo and video content brings your artistic vision to life across all channels.
                </p>
              </div>
            </div>

            {/* Arrow */}
            <div className="flex justify-center">
              <ArrowRight size={32} className="text-white/30 rotate-90" />
            </div>

            {/* Step 4 */}
            <div className="flex gap-6 items-start fade-in-section">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-white text-black flex items-center justify-center font-bold text-lg">
                4
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-3">Perform</h3>
                <p className="text-white/70 leading-relaxed">
                  Our booking agencies get you on the world's best stages. With comprehensive artist 
                  development and management, we ensure your live shows are unforgettable.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* BEKÂR App Section */}
        <div className="bg-gradient-to-br from-zinc-900 to-black border border-white/10 rounded-3xl p-12 text-center mb-20">
          <div className="max-w-3xl mx-auto">
            <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-md flex items-center justify-center mx-auto mb-6">
              <Sparkles size={32} className="text-white" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              The BEKÂR App
            </h2>
            <p className="text-xl text-white/70 leading-relaxed mb-8">
              Your career command center. Manage everything from releases and analytics to bookings 
              and royalties in one beautiful, intuitive app. A rooftop view of your entire career, 
              designed to feel like flying.
            </p>
            <Button 
              size="lg"
              variant="outline"
              className="border-2 border-white/20 text-white hover:bg-white hover:text-black rounded-full px-8 transition-all duration-300 hover:scale-105"
            >
              Coming Soon
            </Button>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready To Experience The Full Ecosystem?
          </h2>
          <p className="text-xl text-white/70 mb-8 max-w-2xl mx-auto">
            Join artists who are already benefiting from our complete 360° approach to music careers.
          </p>
          <Button 
            size="lg"
            className="bg-white text-black hover:bg-white/90 rounded-full px-8 transition-all duration-300 hover:scale-105 shadow-2xl shadow-white/10"
            onClick={() => window.location.href = '/contact'}
          >
            Get Started Today
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};
